import { ITour } from "./tours";

export interface ILocation {
    id: string;
    text: string;
    isAny: boolean;
}

export interface LocationSelections {
    Country: ILocation | null;
    State: ILocation | null;
    Region: ILocation | null;
    Locality: ILocation | null;
}
/**
 * Shape of `GET /products/home/locations` (FT). Pre-joined location tree
 * with TDMS-compatible integer IDs. Some countries skip the `states`
 * level and place `regions` directly under the country (e.g. Cambodia,
 * Costa Rica) — `states` is therefore optional.
 */
export interface ILocationTreeLocality {
    id: number;
    text: string;
}
export interface ILocationTreeRegion {
    id: number;
    text: string;
    locations?: ILocationTreeLocality[];
}
export interface ILocationTreeState {
    id: number;
    text: string;
    regions?: ILocationTreeRegion[];
}
export interface ILocationTreeCountry {
    id: number;
    text: string;
    states?: ILocationTreeState[];
    regions?: ILocationTreeRegion[];
}

export interface LocationState {
    country: ILocation[];
    isLoadingCountry: boolean;
    countryError: string | null;
    state: ILocation[];
    isLoadingState: boolean;
    stateError: string | null;
    region: ILocation[];
    isLoadingRegion: boolean;
    regionError: string | null;
    locality: ILocation[];
    isLoadingLocality: boolean;
    localityError: string | null;
    allLocations: ILocationTreeCountry[];
    isLoadingAllLocations: boolean;
    allLocationsError: string | null;
    selectedLocations: {
        country: ILocation | null;
        state: ILocation | null;
        region: ILocation | null;
        locality: ILocation | null;
    };
}

export interface CategoryData {
    label: string;
    label_id: number;
    products: Array<{
        tdms_product_id: string;
        json: {
            name: string;
            country: string;
            durationDays: number;
            durationNight: number;
            rrp: number;
            productLargeSizeImagePath: string;
            productSmallToMediumSizeImagePath: string;
        };
    }>;
}

export interface CategoryTours {
    categoryName: string;
    tours: ITour[];
    labelId?: number;
}

export interface CategoryDataByLabel {
    productId: string;
    name: string;
    country: string;
    durationDays: number;
    durationNight: number;
    rrp: number | string;
    productSmallToMediumSizeImagePath: string;
    productLargeSizeImagePath: string;
}

export interface FetchProductParams {
    chipType?: string;
    selectedLabel?: string;
    countries?: number | string;
    states?: number | string;
    regions?: number | string;
    locality?: number | string;
    filters?: { [categoryType: string]: Array<number | string> };
}

export interface HomePageSchema {
    success: boolean;
    title: string;
    code: number;
    data: Array<{
        type: string;
        labels: Array<{
            label: string;
            categories: Array<{
                category_id: string;
                category_type: string;
                category: string;
            }>;
        }>;
    }>;
}

export interface HomePageProductState {
    homePageProducts: Record<string, CategoryTours>;
    cache: Record<string, Record<string, CategoryTours>>;
    homePageSchema?: HomePageSchema | null;
    selectedChipType: string;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
    currentCacheKey: string | null;
}

export const sectionConfigs = [
    {
        key: "Country" as const,
        loadingKey: "isLoadingCountry",
        errorKey: "countryError",
        resetAfter: ["State", "Region", "Locality"] as const,
    },
    {
        key: "State" as const,
        loadingKey: "isLoadingState",
        errorKey: "stateError",
        resetAfter: ["Region", "Locality"] as const,
    },
    {
        key: "Region" as const,
        loadingKey: "isLoadingRegion",
        errorKey: "regionError",
        resetAfter: ["Locality"] as const,
    },
    {
        key: "Locality" as const,
        loadingKey: "isLoadingLocality",
        errorKey: "localityError",
        resetAfter: [] as const,
    },
];

export type SectionKey = (typeof sectionConfigs)[number]["key"];
